<template>
    <card title="Drug Shortage Report">
        <!-- Filter -->
        <b-row>
            <b-col>
                <SearchComponent v-model="storeFilter" :prepopulate="true" label="Store" :search-u-r-l="`/store`"
                    :clearable="true"></SearchComponent>
            </b-col>
            <b-col>
                <SearchComponent v-model="programFilter" prepopulate label="Program" search-u-r-l="/Program" clearable>
                </SearchComponent>
            </b-col>
            <b-col cols="12">
                <b-button variant="info" @click="loadItems">Search</b-button>
            </b-col>
        </b-row>
        <hr>

        <!-- Actions -->
        <div class="d-flex justify-content-between">
            <b-button variant="info" size="sm" @click="newDrugShortageReport()">Add Shortage Report</b-button>
            <ExportDropdown size="sm" :disabled="!items.length" @export-pdf="savePDF" @export-csv="saveCSV" />
        </div>

        <b-table striped :items="items" :fields="fields" :busy="isLoading" :sort-by.sync="sortBy" show-empty
            empty-text="No Drug Shortage Reports." :tbody-tr-class="colorCodeTable">
            <template #table-busy>
                <div class="text-center my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #cell(created)="data">
                <b v-if="data.item.canceled || data.item.submitted" v-b-tooltip.hover :title="rowTitle(data.item)">
                    {{ data.item.created | formatDate('lll') }}
                </b>
                <span v-else>
                    {{ data.item.created | formatDate('lll') }}
                </span>
            </template>
            <template #cell(actions)="data">
                <a v-if="data.item.dateOrdered == null" href="#" @click.prevent="removeItem(data.item)"
                    class="text-danger pr-2">
                    <b-icon icon="x-circle" v-b-tooltip.hover title="Remove"></b-icon>
                </a>
                <a v-if="data.item.dateOrdered != null" href="#" @click.prevent="markDelivered(data.item)"
                    class="text-danger pr-2">
                    <b-icon icon="truck" v-b-tooltip.hover title="Delivered"></b-icon>
                </a>
                <a href="#" @click.prevent="openDetails(data.item)">
                    <b-icon icon="pencil-square" v-b-tooltip.hover title="Details"></b-icon>
                </a>
            </template>
        </b-table>

        <!-- Add shortage report Form -->
        <b-modal id="drug-shortage-report-form" title="Drug Shortage Report" size="xl" hide-footer>

            <DrugShortageReportFormComponent v-model="item" @itemSaved='itemSaved'></DrugShortageReportFormComponent>

        </b-modal>
    </card>
</template>
<script lang="ts">
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { formatDateAndTime } from '@/util/DateUtils';
import SearchComponent from '@/components/SearchComponent.vue';
import { DatePicker } from "element-ui";
import DrugShortageReportFormComponent from '@/components/DrugShortageReportFormComponent.vue';
import ExportDropdown from '@/components/ExportDropdown.vue';
import { ExportField, ReportExporter } from '@/mixins/ReportExporter';

import { DrugShortageReport } from '@/models/DrugShortageReport'
import { Store } from '@/models/Store'
import { Program } from '@/models/Program';
import { Vendor } from '@/models/Vendor'
import { Drug } from '@/models/Drug/Drug';
import { Inventory } from '@/models/Inventory';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import moment from 'moment';

@Component({
    name: "DrugShortageReportPage",
    components: {
        SearchComponent,
        Checkbox,
        DrugShortageReportFormComponent,
        [DatePicker.name]: DatePicker,
        ExportDropdown,
    },
})
export default class DrugShortageReportPage extends Mixins(ReportExporter) {
    private isLoading = false;
    private items: Array<DrugShortageReport> = new Array<DrugShortageReport>();
    private item: DrugShortageReport = new DrugShortageReport();

    private storeFilter: Store = new Store();
    private programFilter: Program = new Program();
    private drugFilter: Drug = new Drug();
    private drugShortageReport: DrugShortageReport = {} as DrugShortageReport;

    private fields: any[] = [
        // { key: "id", label: 'ShortageId'},
        { key: "ndc", label: "NDC", formatter: this.ndcFormatter },
        { key: "drugName", label: "Drug" },
        { key: "manufacturer", label: "Vendor" },
        { key: "qtyOnHand", label: "Quantity OnHand" },
        { key: "qtyNeeded", label: "Quantity Needed" },
        { key: "dateOrdered", label: "Date Ordered", formatter: this.orderDateFormatter },
        { key: "estimatedArrivalDate", label: "Estimated Arrival Date", formatter: this.etaDateFormatter },
        // { key: "dateDelivered", label: "DateDelivered" },
        // { key: "backordered", label: "On Backorder" },
        { key: "created", label: "Created" },
        { key: "createdBy", label: "Created By" },
        { key: "actions", label: '' },
    ];
    private sortBy: string = 'created'
    private showSubmitted = false;
    private showRemoved = false;

    created() {
        this.loadItems();
    }

    loadItems() {
        if (!this.storeFilter || !this.storeFilter.id) {
            this.$bvModal.msgBoxOk(`Store needs to be selected`);
            return;
        }

        this.isLoading = true;
        const config: AxiosRequestConfig = {
            params: {
                storeId: this.storeFilter.id,
                programId: this.programFilter?.id
            }
        };
        axios.get<Array<DrugShortageReport>>('DrugShortageReport', config)
            .then(res => {
                this.items = res.data.map(i => new DrugShortageReport(i));
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    itemSaved() {
        this.$bvModal.hide('drug-shortage-report-form');
        this.loadItems();
    }

    get exportFields(): ExportField[] {
        // The table renders "created" through a slot, so the export supplies its own formatter.
        return this.ToExportFields(this.fields, {
            actions: null,
            created: { formatter: formatDateAndTime },
        });
    }

    savePDF() {
        this.ExportDataPDF("Drug Shortage Report", this.items, this.exportFields);
    }

    saveCSV() {
        this.ExportCSV("Drug Shortage Report", this.items, this.exportFields);
    }

    ndcFormatter(value: string) {
        // Exports collapse a missing value to an empty string before the formatter runs, so a
        // `== null` guard falls through and renders "--" for a drug that simply has no NDC.
        if (!value) {
            return "";
        }
        return `${value.substr(0, 5)}-${value.substr(5, 4)}-${value.substr(9)}`;
    };

    orderDateFormatter(value: Date, key: string, item: any) {
        if (!value || item.dateOrdered > item.dateOrdered) {
            return "";
        }
        return moment.utc(value).format("L");
    }

    etaDateFormatter(value: Date, key: string, item: any) {
        if (!value || item.estimatedArrivalDate > item.estimatedArrivalDate) {
            return "";
        }
        return moment.utc(value).format("L");
    }

    colorCodeTable(row: DrugShortageReport, type: string) {
        if (!row || type !== 'row')
            return;
        if (row.backordered != null && row.backordered) return 'table-danger';
        else if (row.dateOrdered != null && row.dateOrdered) return 'table-success';
        else return 'table-info';
    }

    openDetails(item: DrugShortageReport) {
        this.item = item;
        this.$bvModal.show('drug-shortage-report-form');
    }

    removeItem(item: DrugShortageReport) {
        axios.post(`DrugShortageREport/RemoveReport/${item.id}`)
            .catch(err => this.$notification(NotificationOptions.error(err)))
            .finally(() => { this.loadItems(); });
    }

    markDelivered(item: DrugShortageReport) {
        axios.post(`DrugShortageReport/Delivered/${item.id}`)
            .catch(err => this.$notification(NotificationOptions.error(err)))
            .finally(() => { this.loadItems(); });
    }

    newDrugShortageReport() {
        this.item = new DrugShortageReport();
        this.item.qtyOnHand = 0;
        this.$bvModal.show('drug-shortage-report-form');
    }
}
</script>
<style scoped></style>
